import React, { useState } from 'react';
import { Theme } from '@mui/material';
import GVChip from 'components/GVChip/GVChip';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { makeStyles } from 'tss-react/mui';
import styles from './ContextTag.module.css';

interface ContextTagProps {
  label: string;
  color: string;
  tooltip?: string;
  // Actionable props
  actionable?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
  hoverIcon?: React.ReactNode;
  /** When set with actionable tag, replaces `label` while hovered (e.g. Undo Match). */
  hoverLabel?: string;
  /** Hides the label text and keeps only the icon, for cards too narrow to fit the full tag. */
  iconOnly?: boolean;
  testId?: string;
}

const useStyles = makeStyles()((theme: Theme) => {
  return {
    tag: {
      borderRadius: '6px',
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      border: `1.5px solid black`,
      gap: '10px',
      '& .MuiChip-label': {
        padding: '2px 7px 3px 7px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& .labelRow': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        lineHeight: 1,
        minHeight: '14px',
      },
      '& .labelRow .icon-container': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 0,
        '& svg': {
          display: 'block',
        },
      },
      '& .labelRow .labelText': {
        lineHeight: '14px',
        display: 'inline-flex',
        alignItems: 'center',
      },
    },
    tooltipWrapper: {
      display: 'inline-block',
      width: 'fit-content',
      height: 'fit-content',
      padding: 0,
      margin: 0,
      lineHeight: 0,
      fontSize: 0,
      border: 'none',
      overflow: 'visible',
    },
    actionableTag: {
      border: '1.0px solid var(--gds-color-cool-gray-900, #101113)',
      transition: 'background-color 0.2s ease, color 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#D32F2F !important',
      },
      '& .MuiChip-label': {
        letterSpacing: '0.5px',
      },
    },
    chipLabel: {
      display: 'block',
      alignItems: 'initial',
      gap: '0',
    },
    actionableChipLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    resyncTag: {
      border: '1px solid var(--gds-color-cool-gray-900, #101113)',
    },
  };
});

const ContextTag = ({
  label,
  color,
  tooltip,
  actionable = false,
  onClick,
  icon,
  hoverIcon,
  hoverLabel,
  iconOnly = false,
  testId = 'context_tag',
}: ContextTagProps) => {
  const { classes, cx } = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const showIconOnly = iconOnly && !!(icon || hoverIcon);

  const displayLabel = actionable && isHovered && hoverLabel !== undefined && hoverLabel !== '' ? hoverLabel : label;

  const getFontColor = () => {
    switch (color) {
      case 'Grey':
        return '#8792AB';
      case 'Red':
        return '#EA3632';
      case 'Orange':
        return '#FF9800';
      case 'Green':
        return '#2ED2AB';
      case 'white':
        return 'rgba(255, 255, 255, 0.87)';
      default:
        return color;
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (actionable && onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  const chipLabel = (
    <span className="labelRow" aria-label={showIconOnly ? displayLabel : undefined}>
      {(icon || hoverIcon) && (
        <span className="icon-container">{actionable && isHovered && hoverIcon ? hoverIcon : icon}</span>
      )}
      {!showIconOnly && <span className="labelText">{displayLabel}</span>}
    </span>
  );

  const chipProps = {
    'data-testid': testId,
    className: cx(classes.tag, {
      [classes.actionableTag]: actionable,
      [classes.resyncTag]: label === 'RE-INSPECTED',
    }),
    label: chipLabel,
    sx: {
      backgroundColor: 'var(--gds-color-cool-gray-700) !important',
      cursor: actionable ? 'pointer' : 'default',
      '& .MuiChip-label': {
        color: getFontColor(),
        letterSpacing: '0px',
        lineHeight: '16px',
        fontSize: '10px',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& .MuiChip-label .labelText': {
        color: 'inherit',
      },
      ...(actionable && {
        '&:hover .MuiChip-label': {
          color: 'rgba(255, 255, 255, 0.87) !important',
        },
        '&:hover .labelRow .icon-container': {
          color: 'rgba(255, 255, 255, 0.87)',
        },
        '&:hover .labelRow .icon-container svg': {
          color: 'inherit',
          fill: 'currentColor',
        },
      }),
    },
    // Click handler for actionable type
    ...(actionable &&
      onClick && {
        onClick: handleClick,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        role: 'button',
        tabIndex: 0,
      }),
  };

  const chipElement = <GVChip {...chipProps} />;

  return tooltip ? (
    <GVTooltip
      title={
        <div style={{ whiteSpace: 'pre-line', textAlign: label === 'RE-INSPECTED' ? 'center' : 'left' }}>{tooltip}</div>
      }
      side={actionable ? 'top' : 'bottom'}
      className={styles.tooltip}
      sideOffset={2}
    >
      <span className={classes.tooltipWrapper}>{chipElement}</span>
    </GVTooltip>
  ) : (
    chipElement
  );
};

export default ContextTag;
